import { Badge, IconButton, MenuItem } from "@mui/material";
import { Fragment, useState, type ReactNode } from "react";
import { useGomakeAxios, useGomakeRouter } from "@/hooks";
import { GoMakeMenu } from "@/components";
import { useRecoilState } from "recoil";
import { useTranslation } from "react-i18next";
import { FONT_FAMILY } from "@/utils/font-family";
import { useStyle } from "../style";
import QuestionMarkRoundedIcon from "@mui/icons-material/QuestionMarkRounded";
import { CustomTooltip } from "../custom-tooltip";
import { useTheme } from "@/contexts/theme/useTheme";
import { WhatsNewModal } from "./whats-new-modal";
import { useModal } from "@/hooks/use-modal";
import { lastVersionState } from "@/store/last-version-state";
import { updateUserVersionApi } from "@/services/api-service/gomake-versioning/gomake-versioning-endpoint";
import { generateCustomerLoginLinkApi } from "@/services/api-service/support/support-endpoints";
import { TermModal } from "@/widgets/login/terms-modal/TermModal";
import { HpPrintOsSupportIcon } from "@/themes/hp-client/icons";
import { HP_HEADER_DOT_BADGE_SX } from "@/themes/hp-client/components/hp-top-header";

const PRINTOS_SUPPORT_URL = "https://www.printos.com/home/#/ask-the-expert";

type HelpMenuItem = {
  key: string;
  name: string;
  onclick: () => void;
  icon?: ReactNode;
  showWhatsNewBadge?: boolean;
};

interface ISystemHelpProps {
  /** Smaller trigger button, used by the mobile header. */
  compact?: boolean;
}

const SystemHelp = ({ compact }: ISystemHelpProps = {}) => {
  const { t } = useTranslation();
  const { classes } = useStyle();
  const { navigate } = useGomakeRouter();
  const whatsNewModal = useModal();
  const termsModal = useModal();
  const [version, setVersion] = useRecoilState(lastVersionState);
  const { callApi } = useGomakeAxios();
  const { getIconOverride, currentTheme } = useTheme();
  const isHpClient = currentTheme === "hp-client";

  // Get theme-aware Help icon (HP Client icon when theme is active)
  const HpHelpIcon = getIconOverride("HelpIcon");

  // Make background color theme-aware: transparent for HP theme, default gray for others
  const iconButtonBackground =
    currentTheme === "hp-client" ? "transparent" : "#f6f6f6";
  const iconSize = compact ? 18 : 22;

  const [anchorSupportEl, setAnchorSupportEl] = useState<null | HTMLElement>(
    null,
  );
  const openSupport = Boolean(anchorSupportEl);

  const handleClickSupport = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorSupportEl(event.currentTarget);
  };

  const handleCloseSupport = () => {
    setAnchorSupportEl(null);
  };

  const updateUserVersion = async () => {
    await updateUserVersionApi(callApi, (res) => {});
  };

  const handleWhatsNewClick = async () => {
    if (version?.isShowWhatsNewBadge) {
      await updateUserVersion();
      setVersion({
        ...version,
        isShowWhatsNewBadge: false,
        whatsNewBadgeCount: 0,
      });
    }
    whatsNewModal.open();
  };

  const handleGoSupportClick = async (redirect?: "tickets" | "articles") => {
    await generateCustomerLoginLinkApi(
      callApi,
      (res) => {
        if (res?.success && res?.data) {
          window.open(res.data, "_blank");
        }
      },
      redirect ? { redirect } : undefined,
    );
  };

  const handleTermsClick = () => {
    handleCloseSupport();
    termsModal.open();
  };

  const handlePrintOsSupportClick = () => {
    handleCloseSupport();
    window.open(PRINTOS_SUPPORT_URL, "_blank", "noopener,noreferrer");
  };

  const helpMenuList: HelpMenuItem[] = [
    {
      key: "guide",
      name: t("header.guideAndTutorial"),
      onclick: () => navigate("/help-center"),
    },
    {
      key: "whatsNew",
      name: t("header.whatsNew"),
      onclick: handleWhatsNewClick,
      showWhatsNewBadge: true,
    },
    isHpClient
      ? {
          key: "printOsSupport",
          name: t("header.openPrintOsSupport", {
            defaultValue: "Open PrintOS Support",
          }),
          onclick: handlePrintOsSupportClick,
          icon: <HpPrintOsSupportIcon width={24} height={24} />,
        }
      : {
          key: "myTickets",
          name: t("tabs.myTickets"),
          onclick: () => navigate("/my-tickets"),
        },
    {
      key: "terms",
      name: t("tabs.gomakeTerms"),
      onclick: handleTermsClick,
    },
  ];

  return (
    <Badge
      color="error"
      overlap="circular"
      variant="dot"
      invisible={!version?.isShowWhatsNewBadge}
      sx={isHpClient ? HP_HEADER_DOT_BADGE_SX : undefined}
    >
      <CustomTooltip title={t("header.help")} arrow>
        <IconButton
          data-gm-action="help"
          style={{
            backgroundColor: iconButtonBackground,
            padding: compact ? "4px" : "8px",
          }}
          data-tour={"start-tour-btn"}
          onClick={handleClickSupport}
        >
          {HpHelpIcon ? (
            <HpHelpIcon width={iconSize} height={iconSize} />
          ) : (
            <QuestionMarkRoundedIcon
              style={{ width: iconSize, height: iconSize }}
            />
          )}
        </IconButton>
      </CustomTooltip>
      <GoMakeMenu
        handleClose={handleCloseSupport}
        open={openSupport}
        anchorEl={anchorSupportEl}
        noListPadding={true}
        menuOffsetY={1}
      >
        {helpMenuList?.map((item, index) => {
          return (
            <Fragment key={item.key}>
              <MenuItem
                onClick={item?.onclick}
                style={{
                  ...classes.menuItem,
                  ...(isHpClient && index === helpMenuList.length - 1
                    ? { borderBottom: "none" }
                    : null),
                }}
              >
                <div style={classes.menuItemContent}>
                  <div style={classes.logoutContainer}>{item?.name}</div>
                  {item.icon ? (
                    <span style={classes.menuItemIcon}>{item.icon}</span>
                  ) : null}
                  {item.showWhatsNewBadge && (
                    <Badge
                      invisible={!version?.isShowWhatsNewBadge}
                      badgeContent={
                        version?.whatsNewBadgeCount > 1
                          ? `${version.whatsNewBadgeCount}+`
                          : version?.whatsNewBadgeCount
                      }
                      color="error"
                      sx={{
                        "& .MuiBadge-badge": {
                          ...(t("direction") === "rtl"
                            ? { right: -10 }
                            : { right: 10 }),
                          ...FONT_FAMILY.Lexend(400, 13),
                          height: "18px",
                          minWidth: "18px",
                          borderRadius: "9px",
                          padding: "0 8px",
                        },
                      }}
                    />
                  )}
                </div>
              </MenuItem>
              {index != helpMenuList?.length - 1 ? (
                <div style={classes.lineContainer} />
              ) : null}
            </Fragment>
          );
        })}
      </GoMakeMenu>
      <WhatsNewModal
        open={whatsNewModal.isOpen}
        onClose={whatsNewModal.close}
        version={version}
      />
      <TermModal
        open={termsModal.isOpen}
        onClose={termsModal.close}
        setIsTermsAccepted={() => {}}
        setIsModalOpen={termsModal.close}
        isQuickSetup={true}
      />
    </Badge>
  );
};
export { SystemHelp };
